import { BaseDatasetDropzone } from "../BaseDatasetDropzone/BaseDatasetDropzone";
import posthogEvents from "@app/config/posthog/posthog-events";
import { useCreateExperimentDraft, useExperimentListWithSorting } from "@app/queries/experiment";
import { useFavorites, useRecents } from "@app/queries/iam";
import { useSearch } from "@app/queries/search";
import type { DatasetSearchResult, Experiment, ExperimentId, ExperimentSearchResult } from "@globals";
import Icon from "@lib/common/components/Icon/Icon";
import TimeAgo from "@lib/common/components/TimeAgo/TimeAgo";
import buildMatchSnippet from "@lib/common/utils/buildMatchSnippet";
import DatasetIcon from "@lib/dataset/DatasetIcon/DatasetIcon";
import ExperimentIcon from "@lib/experiment/ExperimentIcon/ExperimentIcon";
import ExperimentStatusBadge from "@lib/experiment/ExperimentStatusBadge/ExperimentStatusBadge";
import formatExperimentName from "@lib/experiment/utils/formatExperimentName";
import resolveExperimentLinkTo from "@lib/experiment/utils/resolveExperimentLinkTo";
import { Group, Skeleton, Stack, VisuallyHidden } from "@mantine/core";
import { useDebouncedValue, useHotkeys } from "@mantine/hooks";
import type { SpotlightActionData, SpotlightActionGroupData, SpotlightFilterFunction } from "@mantine/spotlight";
import { Spotlight, spotlight } from "@mantine/spotlight";
import { sortBy, uniq } from "lodash-es";
import { usePostHog } from "posthog-js/react";
import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";

// "name" isn't listed here since it's already visible as the item's label and needs no caption.
const MATCHED_FIELD_LABEL: Record<string, string> = {
  description: "description",
  expert_context: "expert context",
  variable: "variable",
  result: "result",
  constraint: "constraint",
  created_by: "created by"
};

function matchCaption(matchedField: string, matchedValue: string | null, query: string): string | undefined {
  const label = MATCHED_FIELD_LABEL[matchedField];
  if (!label || !matchedValue) {
    return undefined;
  }
  return `Matched ${label}: "${buildMatchSnippet(matchedValue, query)}"`;
}

export default function SpotlightSearch() {
  const [query, setQuery] = useState("");
  const [searchQuery] = useDebouncedValue(query, 300);
  const navigate = useNavigate();
  const posthog = usePostHog();
  useHotkeys(
    [
      [
        "mod+K",
        () => {
          spotlight.toggle();
          posthog.capture(posthogEvents.spotlight_search.toggle_by_hotkey);
        }
      ]
    ],
    []
  );
  const dropzoneRef = useRef<() => void>(null);
  const createExperimentDraft = useCreateExperimentDraft();

  const createAndNavigateToDraft = useCallback(async () => {
    const draft = await createExperimentDraft.mutateAsync({});
    await navigate(`/experiments/drafts/${draft.id}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const favoriteExperimentsQuery = useFavorites();
  const recentExperimentsQuery = useRecents();
  const experimentsQuery = useExperimentListWithSorting({
    limit: 20,
    offset: 0,
    sort_field: "updated_at",
    sort_order: "desc",
    created_by: undefined,
    exclude_drafts: false
  });
  const searchQueryResult = useSearch(searchQuery);

  const shortcutActions: SpotlightActionData[] = useMemo(
    () => [
      {
        id: "home",
        label: "Home",
        leftSection: <Icon name="home" color="dimmed" />,
        rightSection: <Icon name="arrow-right" color="dimmed" size="xs" />,
        onClick: () => {
          posthog.capture(posthogEvents.spotlight_search.home_click);
          void navigate(`/`);
        }
      },
      {
        id: "new-experiment",
        label: "New experiment",
        leftSection: <Icon name="plus" color="dimmed" />,
        rightSection: <Icon name="arrow-right" color="dimmed" size="xs" />,
        onClick: () => {
          posthog.capture(posthogEvents.spotlight_search.new_experiment_click);
          void createAndNavigateToDraft();
        }
      },
      {
        id: "upload-dataset",
        label: "Upload dataset",
        leftSection: <Icon name="upload" color="dimmed" />,
        rightSection: <Icon name="arrow-right" color="dimmed" size="xs" />,
        onClick: () => {
          posthog.capture(posthogEvents.spotlight_search.upload_dataset_click);
          dropzoneRef.current?.();
        }
      }
    ],
    [navigate, createAndNavigateToDraft, posthog]
  );

  function experimentActionData(
    experiment: Pick<Experiment, "id" | "name" | "status" | "updated_at" | "is_favorite">,
    posthogEvent: keyof (typeof posthogEvents)["spotlight_search"],
    description?: string
  ): SpotlightActionData {
    const { id, name, status, updated_at, is_favorite } = experiment;
    return {
      id,
      label: formatExperimentName(name),
      description,
      leftSection: <ExperimentIcon status={status} size="xs" />,
      rightSection: (
        <Group gap="sm" wrap="nowrap">
          {is_favorite && <Icon name="star" size="xs" color="yellow" variant="solid" />}
          <ExperimentStatusBadge status={status} size="xs" />
          <TimeAgo date={new Date(updated_at)} size="xs" c="dimmed" lineClamp={1} lh={1} />
        </Group>
      ),
      onClick: () => {
        posthog.capture(posthogEvents.spotlight_search[posthogEvent]);
        void navigate(resolveExperimentLinkTo(id, status));
      }
    };
  }

  function datasetActionData(dataset: DatasetSearchResult): SpotlightActionData {
    const { id, name, matched_field, matched_value } = dataset;
    return {
      id,
      label: name,
      description: matchCaption(matched_field, matched_value ?? null, searchQuery),
      leftSection: <DatasetIcon size="xs" />,
      onClick: () => {
        posthog.capture(posthogEvents.spotlight_search.search_dataset_click);
        void navigate(`/datasets/${id}`);
      }
    };
  }

  function buildSearchActions(): {
    actions: Array<SpotlightActionGroupData | SpotlightActionData>;
    highlightQuery: boolean;
  } {
    // Mantine Spotlight unmounts its actions list when it's empty, resetting its internal `listId`
    // to "". Pressing Enter then builds the invalid selector `# [data-selected]` and throws a
    // SyntaxError. Returning an inert stub keeps the list mounted so Enter is a safe no-op.
    const noResultsStub = (label: string, highlightQuery: boolean) => ({
      actions: [{ id: "spotlight-no-results", label, onClick: () => {} }],
      highlightQuery
    });

    const searchingStub = () => ({
      actions: Array.from({ length: 5 }, (_, index) => ({
        id: `spotlight-skeleton-${index}`,
        onClick: () => {},
        style: index < 4 ? { marginBottom: "var(--mantine-spacing-xs)" } : undefined,
        children: (
          <Group gap="sm" wrap="nowrap" w="100%" py="xs">
            <Skeleton height={24} width={24} radius="sm" animate />
            <Stack gap="xs" flex={1}>
              <Skeleton height={12} w="45%" animate />
              <Skeleton height={10} w="70%" animate />
            </Stack>
          </Group>
        )
      })),
      highlightQuery: false
    });

    // `searchQuery` (and thus `searchQueryResult`) lags behind `query` until the debounce fires, so
    // until they match, the results/`isFetching` we have belong to a stale term. Bail out early
    // instead of rendering stale results for a different term.
    if (query.trim() !== searchQuery.trim()) {
      return searchingStub();
    }

    // Search results are already tier/priority-sorted server-side; render as-is.
    const actions: Array<SpotlightActionGroupData | SpotlightActionData> = [];
    const experimentResults = searchQueryResult.data?.experiments ?? [];
    const datasetResults = searchQueryResult.data?.datasets ?? [];

    if (experimentResults.length > 0) {
      actions.push({
        group: "Experiments",
        actions: experimentResults.map((experiment: ExperimentSearchResult) =>
          experimentActionData(
            experiment,
            "search_experiment_click",
            matchCaption(experiment.matched_field, experiment.matched_value ?? null, searchQuery)
          )
        )
      });
    }

    if (datasetResults.length > 0) {
      actions.push({
        group: "Datasets",
        actions: datasetResults.map(datasetActionData)
      });
    }

    if (actions.length > 0) {
      return { actions, highlightQuery: true };
    }

    return searchQueryResult.isFetching ? searchingStub() : noResultsStub("Nothing found", true);
  }

  function buildIdleActions(): Array<SpotlightActionGroupData | SpotlightActionData> {
    const actions: Array<SpotlightActionGroupData | SpotlightActionData> = [...shortcutActions];

    if (!favoriteExperimentsQuery.data || !recentExperimentsQuery.data || !experimentsQuery.data.results) {
      return actions;
    }

    const favorites = sortBy(favoriteExperimentsQuery.data.experiments, "updated_at")
      .reverse()
      .map(experiment => experimentActionData(experiment, "favorite_experiment_click"));

    // Filter out Favorites from Recent.
    const recent = sortBy(
      recentExperimentsQuery.data.experiments.filter(({ id }) => !favorites.find(exp => exp.id === id)),
      "updated_at"
    )
      .reverse()
      .map(experiment => experimentActionData(experiment, "recent_experiment_click"));

    // All other experiments not Favorite or Recent.
    const excludeFromRest: Array<ExperimentId> = uniq([...favorites.map(exp => exp.id), ...recent.map(exp => exp.id)]);
    const rest = sortBy(
      experimentsQuery.data.results.filter(exp => !excludeFromRest.includes(exp.id)),
      "updated_at"
    )
      .reverse()
      .map(experiment => experimentActionData(experiment, "other_experiment_click"));

    if (favorites.length > 0) {
      actions.push({ group: "Favorites", actions: favorites });
    }
    if (recent.length > 0) {
      actions.push({ group: "Recent", actions: recent });
    }
    if (rest.length > 0) {
      actions.push({ group: "Other experiments", actions: rest });
    }
    return actions;
  }

  const { actions: spotlightActions, highlightQuery } = useMemo(() => {
    // False positive: `dropzoneRef` is only read inside an onClick, never during this computation.
    // eslint-disable-next-line react-hooks/refs
    return query.trim() ? buildSearchActions() : { actions: buildIdleActions(), highlightQuery: true };
  }, [
    query,
    searchQuery,
    experimentsQuery.data,
    favoriteExperimentsQuery.data,
    recentExperimentsQuery.data,
    searchQueryResult.data,
    searchQueryResult.isFetching,
    shortcutActions
  ]);

  // `spotlightActions` is already the exact set of actions to show (shortcuts/favorites/recent/other when
  // idle, or server search results, already ranked, when searching) - skip Mantine's default label/description
  // substring filter, which would otherwise re-filter/re-order using only the truncated caption text.
  const passthroughFilter: SpotlightFilterFunction = (_query, actions) => actions;

  return (
    <>
      <Spotlight
        actions={spotlightActions}
        query={query}
        onQueryChange={setQuery}
        filter={passthroughFilter}
        highlightQuery={highlightQuery}
        closeOnClickOutside
        scrollable
        limit={30}
        maxHeight="80vh"
        returnFocus={false}
        searchProps={{
          leftSection: <Icon name="magnifying-glass" />,
          placeholder: "Find experiments and datasets"
        }}
        scrollAreaProps={{ type: "always" }}
        shortcut={[]}
        size="xl"
        styles={{ actionsList: { minHeight: "80vh" } }}
      />
      <VisuallyHidden>
        <BaseDatasetDropzone p={0} openRef={dropzoneRef} />
      </VisuallyHidden>
    </>
  );
}
